import { useEffect, useMemo } from 'react';
import { PageDto } from '@asset-metrix/portal-gateway-client-js';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../store';
import { type FilterSelections } from '../store/states/filterSelection';
import {
  CUSTOM_FILTER_INSTANCE_ID_SUFFIX,
  GLOBAL_SEARCH_URL_PARAM,
} from '../utils/constants';
import { getTableFilterInstanceId } from '../utils/filterInstanceId';
import { getGlobalSearchFiltersFromPage } from '../utils/globalSearchHelpers';
import { getVisibleWidgetIds } from '../utils/pageContentHelpers';

import { presetSelectionFromUrl } from './presetFilterSelectionForFilter';
import { useSearchParamsByKeys } from './useSearchParamsByKey';

export type UsePresetPageFiltersParams = {
  pageData?: PageDto;
  instanceId: string | null;
  persistedSelections?: FilterSelections;
  persistedCustomSelections?: FilterSelections;
  persistedSelectionsLoaded?: boolean;
  activeTabValues?: Record<string, string>;
};

const EMPTY_ACTIVE_TAB_VALUES: Record<string, string> = {};

export const usePresetPageFilters = ({
  pageData,
  instanceId,
  persistedSelections,
  persistedCustomSelections,
  persistedSelectionsLoaded = true,
  activeTabValues = EMPTY_ACTIVE_TAB_VALUES,
}: UsePresetPageFiltersParams) => {
  const [
    filterInitialized,
    presetFilterSelectionValue,
    setFilterInitialized,
    setFilterQueryLoaded,
  ] = useStore(
    useShallow((state) => [
      state.filterInitialized,
      state.presetFilterSelectionValue,
      state.setFilterInitialized,
      state.setFilterQueryLoaded,
    ])
  );

  const config = pageData?.pageFilter;

  const filterKeys = useMemo(
    () => config?.filters.map((f) => f.key) ?? [],
    [config?.filters]
  );

  const allGlobalSearchKeys = useMemo(() => {
    return pageData != null ? getGlobalSearchFiltersFromPage(pageData) : [];
  }, [pageData]);

  const visibleWidgetIds = useMemo(
    () => getVisibleWidgetIds(pageData?.contents ?? [], activeTabValues),
    [pageData?.contents, activeTabValues]
  );

  const globalSearchKeys = useMemo(
    () =>
      allGlobalSearchKeys.filter((entry) =>
        visibleWidgetIds.has(entry.widgetId)
      ),
    [allGlobalSearchKeys, visibleWidgetIds]
  );

  const hasGlobalSearch = globalSearchKeys.length > 0;
  const searchParamKeys = useMemo(
    () =>
      hasGlobalSearch ? [...filterKeys, GLOBAL_SEARCH_URL_PARAM] : filterKeys,
    [filterKeys, hasGlobalSearch]
  );

  const hasCustomPageFilterColumns = useMemo(
    () =>
      config?.customizablePageFilters === true &&
      config?.customPageFilterColumns != null &&
      config.customPageFilterColumns.length > 0,
    [config?.customPageFilterColumns, config?.customizablePageFilters]
  );

  const filtersFromUrl = useSearchParamsByKeys(searchParamKeys);

  useEffect(() => {
    if (
      instanceId == null ||
      filterInitialized[instanceId] === true ||
      pageData == null ||
      !persistedSelectionsLoaded
    ) {
      return;
    }

    for (const fc of config?.filters ?? []) {
      const urlValue = filtersFromUrl[fc.key];
      const hasUrlValue = urlValue != null && urlValue.length > 0;

      if (!hasUrlValue && fc.hidden) {
        console.warn(
          `Hidden page filter ${fc.key} is not preset from the URL. This is highly discouraged.`
        );
      }

      if (hasUrlValue) {
        presetSelectionFromUrl(
          presetFilterSelectionValue,
          instanceId,
          urlValue,
          fc
        );
      } else if (persistedSelections?.[fc.key] != null) {
        presetFilterSelectionValue(
          instanceId,
          fc.key,
          persistedSelections[fc.key]
        );
      }
    }

    const globalSearchUrlValue = filtersFromUrl[GLOBAL_SEARCH_URL_PARAM];
    if (
      globalSearchKeys.length > 0 &&
      globalSearchUrlValue != null &&
      globalSearchUrlValue.length > 0
    ) {
      for (const { widgetId, metadataId } of globalSearchKeys) {
        presetFilterSelectionValue(
          getTableFilterInstanceId(widgetId, instanceId),
          metadataId,
          globalSearchUrlValue[0]
        );
      }
    }

    // Custom filter selections are not added in the URL, so we need to preset them from persisted data.
    if (persistedCustomSelections != null) {
      const customInstanceId = `${instanceId}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`;
      for (const [key, value] of Object.entries(persistedCustomSelections)) {
        if (value != null) {
          presetFilterSelectionValue(customInstanceId, key, value);
        }
      }
    }

    setFilterInitialized(instanceId);

    if ((config?.filters ?? []).length === 0) {
      setFilterQueryLoaded(instanceId);
    }

    if (!hasCustomPageFilterColumns) {
      setFilterQueryLoaded(`${instanceId}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`);
    }
  }, [
    instanceId,
    config?.filters,
    filterInitialized,
    filtersFromUrl,
    globalSearchKeys,
    pageData,
    persistedSelections,
    persistedCustomSelections,
    persistedSelectionsLoaded,
    presetFilterSelectionValue,
    setFilterInitialized,
    setFilterQueryLoaded,
    hasCustomPageFilterColumns,
  ]);
};
